import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import {
  getAuthorPageTextPolicy,
  normalizeAuthorPageVisualUrls,
} from '../../authored/authorPageVisualPolicy';
import { editorDirAttr } from '../../authored/rtlEditorAlign';

const DEFAULT_PAGE_TEXTURE = '/images/clear-white-plaster-texture-pattern.jpg';

/**
 * One panel in an authored-book spread (main or stack layer). Own TipTap instance when text is shown.
 */
export default function AuthoredReaderSpreadLeaf({
  page,
  side,
  layer,
  flipClass = '',
  fixedPanelPx,
  selectedPageTheme,
  innerMainClassName,
  innerStackClassName,
  textStyle,
  extensions,
  pageChrome,
  mainZIndex = 1,
  textDir = 'ltr',
}) {
  const visualUrls = useMemo(() => (page ? normalizeAuthorPageVisualUrls(page) : []), [page]);
  const policy = useMemo(
    () => (page ? getAuthorPageTextPolicy(page, visualUrls) : { layoutMode: 'text', allowText: true, maxWords: 0 }),
    [page, visualUrls],
  );

  /** Full-bleed img is `position:absolute`; host needs explicit px height. Fills inner body minus page footer. */
  const fullBleedFrameHeightPx = useMemo(() => {
    const innerPadY = 64; // INNER_* p-8 top + bottom
    const abovePageNumber = 36; // page number + gap above it
    return Math.max(160, fixedPanelPx - innerPadY - abovePageNumber);
  }, [fixedPanelPx]);

  /** Two half-page slots: each slot only contains an absolute img — without min height the row collapses to 0. */
  const halfTwoSlotHeightPx = useMemo(() => {
    const innerPadY = 64;
    const chrome = 52; // half_two outer padding, gap, margins vs headings
    return Math.max(120, Math.round((fixedPanelPx - innerPadY - chrome) / 2));
  }, [fixedPanelPx]);

  const showEditor =
    page &&
    (policy.layoutMode === 'text' || policy.layoutMode === 'half_one_image') &&
    policy.allowText;

  const editorDir = editorDirAttr(textDir);

  const editor = useEditor(
    {
      immediatelyRender: false,
      editable: false,
      extensions,
      content: { type: 'doc', content: [] },
      editorProps: {
        attributes: {
          class:
            'tiptap-editor max-w-none min-h-0 flex-1 focus:outline-none text-gray-800 leading-relaxed text-shadow',
          dir: editorDir,
        },
      },
    },
    [extensions],
  );

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.setAttribute('dir', editorDir);
  }, [editor, editorDir]);

  useEffect(() => {
    if (!editor || !page || !showEditor) return;
    const json = page.body_json || { type: 'doc', content: [] };
    editor.commands.setContent(json, false);
  }, [editor, page, showEditor]);

  const bgUrl = selectedPageTheme?.theme_url || DEFAULT_PAGE_TEXTURE;
  const isMain = layer === 'main';
  const innerClass = isMain ? innerMainClassName : innerStackClassName;

  const rounded =
    side === 'left'
      ? isMain
        ? 'rounded-l-md'
        : 'rounded-r-md'
      : isMain
        ? 'rounded-r-md'
        : 'rounded-r-md';

  const spineBorderStyle =
    side === 'left'
      ? {
          borderRightWidth: 4,
          borderRightStyle: 'solid',
          borderRightColor: pageChrome?.accentColor ?? '#d1d5db',
        }
      : {
          borderLeftWidth: 4,
          borderLeftStyle: 'solid',
          borderLeftColor: pageChrome?.accentColor ?? '#d1d5db',
        };

  /** Match basicTheme enhanced reader: hinge at the spine so rotateY reads as a page turn. */
  const hingeTransformOrigin =
    isMain && side === 'left' ? 'right center' : isMain && side === 'right' ? 'left center' : undefined;

  const outerPanelStyle = {
    width: fixedPanelPx,
    height: fixedPanelPx,
    zIndex: isMain ? mainZIndex : 0,
    backgroundImage: `url(${bgUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: pageChrome?.surfaceFallback ?? '#f5f5f4',
    ...spineBorderStyle,
    ...(hingeTransformOrigin ? { transformOrigin: hingeTransformOrigin } : {}),
    ...(isMain && page ? { boxShadow: '0 4px 8px rgba(0, 0, 0, 0.25)' } : {}),
  };

  /** Match basicTheme: no stack panel when there is no underlying page. */
  if (!page && !isMain) {
    return null;
  }

  /** Main sheet vs stack (basicTheme: lg on mains, md on stacks). */
  const surfaceClass = isMain ? 'shadow-lg' : 'shadow-md';

  if (!page) {
    return (
      <div
        className={`shrink-0 ${surfaceClass} ${rounded} ${flipClass} ${
          isMain ? 'relative' : `absolute top-0 ${side === 'left' ? 'left-0' : 'right-0'}`
        }`}
        style={outerPanelStyle}
      >
        <div
          className={`${innerClass} flex min-w-0 w-full items-center justify-center text-gray-400 font-serif`}
          style={textStyle}
        >
          {isMain ? '—' : ''}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`shrink-0 ${surfaceClass} ${rounded} ${
        isMain ? 'overflow-hidden' : ''
      } ${flipClass} ${isMain ? 'relative' : `absolute top-0 ${side === 'left' ? 'left-0' : 'right-0'}`}`}
      style={outerPanelStyle}
    >
      <div className={`${innerClass} relative min-h-0 min-w-0 w-full`} style={textStyle}>
        {page.chapter_heading ? (
          <h2 className="mb-2 text-2xl font-bold text-gray-900">{page.chapter_heading}</h2>
        ) : null}
        {page.chapter_subheading ? (
          <h3 className="mb-4 text-lg text-gray-600">{page.chapter_subheading}</h3>
        ) : null}

        {policy.layoutMode === 'half_one_image' && visualUrls[0] ? (
          <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
            <div
              className="flex min-h-[14rem] w-full shrink-0 basis-[56%] flex-col overflow-hidden rounded-lg border border-stone-200 bg-stone-50/90 p-3 shadow-sm md:min-h-[18rem] md:basis-[58%]"
              aria-label="Half-page image"
            >
              <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-md border border-stone-300 bg-stone-200">
                <img
                  src={visualUrls[0]}
                  alt=""
                  className="absolute inset-0 h-full w-full object-contain"
                />
              </div>
            </div>
            {showEditor && editor ? (
              <div className="min-h-0 flex-1 overflow-y-auto border-t border-stone-200/80 pt-2">
                <EditorContent editor={editor} />
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {policy.layoutMode === 'full_bleed' && visualUrls[0] ? (
              <div
                className="-mx-8 box-border flex w-[calc(100%+4rem)] shrink-0 flex-col overflow-hidden rounded-md bg-stone-100 p-2.5 md:p-3"
                style={{ height: fullBleedFrameHeightPx }}
              >
                <div className="relative min-h-0 w-full flex-1 overflow-hidden rounded-sm bg-stone-100">
                  <img
                    src={visualUrls[0]}
                    alt=""
                    className="absolute inset-0 h-full w-full object-contain object-center"
                  />
                </div>
              </div>
            ) : null}

            {policy.layoutMode === 'half_two_images' ? (
              <div className="mb-4 flex shrink-0 flex-col gap-3 overflow-hidden rounded-lg border border-stone-200 bg-stone-50/90 p-3 shadow-sm">
                {[0, 1].map((slot) => (
                  <div
                    key={visualUrls[slot] ?? `slot-${slot}`}
                    className="relative w-full shrink-0 overflow-hidden rounded-md border border-stone-300 bg-stone-200"
                    style={{ height: halfTwoSlotHeightPx }}
                  >
                    {visualUrls[slot] ? (
                      <img
                        src={visualUrls[slot]}
                        alt=""
                        className="absolute inset-0 h-full w-full object-contain"
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {showEditor && editor ? (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <EditorContent editor={editor} />
              </div>
            ) : null}
          </>
        )}
      </div>

      {isMain ? (
        <div className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 text-md font-serif leading-relaxed text-gray-800 text-shadow">
          {page.page_order_no != null ? String(page.page_order_no) : ''}
        </div>
      ) : null}
    </div>
  );
}
